import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  AlertTriangle, Package, MapPin, Wrench, RefreshCw, CheckCircle2,
  FileWarning, Box, Loader2
} from 'lucide-react';

const RepairStockPage = () => {
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState(false);
  const [problematicReceipts, setProblematicReceipts] = useState([]);
  const [unallocatedStock, setUnallocatedStock] = useState([]);
  const [bins, setBins] = useState([]);
  const [selectedBin, setSelectedBin] = useState('');
  const [individualBinSelections, setIndividualBinSelections] = useState({});
  const [repairingEntryId, setRepairingEntryId] = useState(null);
  const [repairResult, setRepairResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch problematic receipts, unallocated stock, and bins in parallel
      const [receiptsRes, unallocatedRes, binsRes] = await Promise.all([
        api.get('/wms/admin/receipts-without-stock'),
        api.get('/wms/admin/stock-without-bin').catch(() => ({ data: { entries: [] } })),
        api.get('/bins')
      ]);
      
      setProblematicReceipts(receiptsRes.data.receipts || []);
      setUnallocatedStock(unallocatedRes.data.entries || []);
      setBins(binsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleIndividualBinChange = (stockEntryId, binId) => {
    setIndividualBinSelections(prev => ({
      ...prev,
      [stockEntryId]: binId
    }));
  };

  const handleAssignIndividualBin = async (stockEntryId) => {
    const binId = individualBinSelections[stockEntryId];
    if (!binId) {
      toast.error('Selectați un BIN pentru acest articol');
      return;
    }

    try {
      setRepairingEntryId(stockEntryId);
      const response = await api.post(`/wms/admin/assign-bin-to-stock?stock_entry_id=${stockEntryId}&bin_id=${binId}`);
      toast.success(response.data.message);
      
      // Remove this entry from the list
      setUnallocatedStock(prev => prev.filter(e => e.stock_entry_id !== stockEntryId));
      // Clear selection
      setIndividualBinSelections(prev => {
        const newSelections = { ...prev };
        delete newSelections[stockEntryId];
        return newSelections;
      });
    } catch (error) {
      console.error('Error assigning bin:', error);
      toast.error(error.response?.data?.detail || 'Eroare la alocare BIN');
    } finally {
      setRepairingEntryId(null);
    }
  };

  const handleRepair = async () => {
    if (!selectedBin) {
      toast.error('Selectați un BIN înainte de reparare');
      return;
    }

    try {
      setRepairing(true);
      const response = await api.post(`/wms/admin/repair-orphaned-receipts?bin_id=${selectedBin}`);
      setRepairResult(response.data);
      toast.success(`Reparare completă: ${response.data.repaired_count} articole reparate`);
      
      // Refresh the list
      await fetchData();
    } catch (error) {
      console.error('Error repairing:', error);
      toast.error(error.response?.data?.detail || 'Eroare la reparare');
    } finally {
      setRepairing(false);
    }
  };

  const selectedBinInfo = bins.find(b => b.id === selectedBin);

  return (
    <div className="space-y-6 p-6" data-testid="repair-stock-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wrench className="w-7 h-7 text-orange-600" />
            Reparare Stoc Lipsă
          </h1>
          <p className="text-slate-500 mt-1">
            Identifică și repară recepțiile care au mișcări de stoc dar nu au intrări efective în stoc
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Reîncarcă
        </Button>
      </div>

      {/* Warning Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-800">Ce face acest instrument?</p>
            <ul className="text-sm text-amber-700 mt-1 space-y-1">
              <li>• Găsește recepțiile care au articole fără alocare BIN</li>
              <li>• Creează intrările de stoc lipsă în BIN-ul selectat</li>
              <li>• Articolele vor apărea corect în Stock Locator și Operațiuni Stoc</li>
            </ul>
          </div>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
            <p className="text-slate-500 mt-2">Se încarcă datele...</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileWarning className="w-5 h-5 text-orange-500" />
                Recepții Problematice
              </CardTitle>
            </CardHeader>
            <CardContent>
              {problematicReceipts.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-lg font-semibold text-green-700">Totul este în ordine!</p>
                  <p className="text-slate-500 mt-1">Nu există recepții cu stoc lipsă.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                    <div>
                      <p className="text-2xl font-bold text-red-700">{problematicReceipts.length}</p>
                      <p className="text-sm text-red-600">recepții cu articole fără stoc</p>
                    </div>
                    <Package className="w-10 h-10 text-red-400" />
                  </div>

                  {/* Repair Section */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="font-semibold text-blue-800 mb-3">Selectați BIN-ul pentru reparare:</p>
                    <div className="flex gap-3 items-end">
                      <div className="flex-1">
                        <Select value={selectedBin} onValueChange={setSelectedBin}>
                          <SelectTrigger className="bg-white" data-testid="bin-select">
                            <SelectValue placeholder="Alegeți un BIN..." />
                          </SelectTrigger>
                          <SelectContent>
                            {bins.map(bin => (
                              <SelectItem key={bin.id} value={bin.id}>
                                <span className="font-mono">{bin.code}</span>
                                <span className="text-slate-500 ml-2">
                                  ({bin.cabinet}/{bin.shelf}/{bin.position})
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedBinInfo && (
                          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {selectedBinInfo.cabinet} → {selectedBinInfo.shelf} → {selectedBinInfo.position}
                          </p>
                        )}
                      </div>
                      <Button 
                        onClick={handleRepair} 
                        disabled={!selectedBin || repairing}
                        className="bg-orange-600 hover:bg-orange-700"
                        data-testid="repair-button"
                      >
                        {repairing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Se repară...
                          </>
                        ) : (
                          <>
                            <Wrench className="w-4 h-4 mr-2" />
                            Repară Toate
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* List of problematic receipts */}
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {problematicReceipts.map(receipt => (
                      <div 
                        key={receipt.receipt_id} 
                        className="p-3 bg-white border rounded-lg hover:border-orange-300 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="font-mono">
                              {receipt.receipt_number}
                            </Badge>
                            <span className="text-sm text-slate-500">
                              Factură: {receipt.invoice_number}
                            </span>
                          </div>
                          <span className="text-xs text-slate-400">
                            {new Date(receipt.created_at).toLocaleDateString('ro-RO')}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          Furnizor: <span className="font-medium">{receipt.supplier_name || 'N/A'}</span>
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {receipt.problematic_items?.map((item, idx) => (
                            <Badge 
                              key={idx} 
                              className="bg-red-100 text-red-700 text-xs"
                            >
                              <Box className="w-3 h-3 mr-1" />
                              {item.article_code || item.article_name?.substring(0, 20)} × {item.quantity}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Repair Result */}
          {repairResult && repairResult.repaired_count > 0 && (
            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-5 h-5" />
                  Rezultat Reparare
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-green-800 mb-3">
                  <span className="font-bold text-2xl">{repairResult.repaired_count}</span> articole au primit intrări de stoc în BIN-ul <span className="font-mono font-semibold">{repairResult.bin_used}</span>
                </p>
                {repairResult.repaired_items?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-green-100">
                        <tr>
                          <th className="text-left p-2">Nr. Recepție</th>
                          <th className="text-left p-2">Articol</th>
                          <th className="text-right p-2">Cantitate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {repairResult.repaired_items.map((item, idx) => (
                          <tr key={idx} className="border-t border-green-200">
                            <td className="p-2 font-mono text-xs">{item.receipt_number}</td>
                            <td className="p-2">
                              <span className="font-mono text-xs text-slate-500">{item.article_code}</span>
                              <span className="ml-2">{item.article_name?.substring(0, 30)}</span>
                            </td>
                            <td className="p-2 text-right font-semibold">{item.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Unallocated Stock Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-purple-500" />
                Stoc Fără BIN Alocat
              </CardTitle>
            </CardHeader>
            <CardContent>
              {unallocatedStock.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-lg font-semibold text-green-700">Totul este în ordine!</p>
                  <p className="text-slate-500 mt-1">Nu există intrări de stoc fără BIN alocat.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div>
                      <p className="text-2xl font-bold text-purple-700">{unallocatedStock.length}</p>
                      <p className="text-sm text-purple-600">intrări de stoc fără BIN - alocați individual</p>
                    </div>
                    <MapPin className="w-10 h-10 text-purple-400" />
                  </div>

                  {/* List of unallocated stock with individual BIN selectors */}
                  <div className="space-y-3">
                    {unallocatedStock.map(entry => (
                      <div 
                        key={entry.stock_entry_id} 
                        className="p-4 bg-white border rounded-lg hover:border-purple-300 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="font-mono text-sm">
                              {entry.article_code}
                            </Badge>
                            <span className="font-medium">
                              {entry.article_name?.substring(0, 50)}
                            </span>
                          </div>
                          <Badge className="bg-purple-100 text-purple-700 text-sm">
                            {entry.quantity} buc
                          </Badge>
                        </div>
                        
                        <div className="text-xs text-slate-500 mb-3 grid grid-cols-3 gap-2">
                          <p>Recepție: <span className="font-mono">{entry.receipt_number}</span></p>
                          <p>Furnizor: {entry.supplier_name || 'N/A'}</p>
                          <p>Lot: {entry.lot_number || 'N/A'}</p>
                        </div>

                        {/* Individual BIN selector */}
                        <div className="flex gap-2 items-center pt-2 border-t">
                          <div className="flex-1">
                            <Select 
                              value={individualBinSelections[entry.stock_entry_id] || ''} 
                              onValueChange={(val) => handleIndividualBinChange(entry.stock_entry_id, val)}
                            >
                              <SelectTrigger className="bg-white h-9" data-testid={`bin-select-${entry.stock_entry_id}`}>
                                <SelectValue placeholder="Alegeți BIN..." />
                              </SelectTrigger>
                              <SelectContent>
                                {bins.map(bin => (
                                  <SelectItem key={bin.id} value={bin.id}>
                                    <span className="font-mono text-sm">{bin.code}</span>
                                    <span className="text-slate-500 ml-2 text-xs">
                                      ({bin.cabinet}/{bin.shelf}/{bin.position})
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button 
                            onClick={() => handleAssignIndividualBin(entry.stock_entry_id)} 
                            disabled={!individualBinSelections[entry.stock_entry_id] || repairingEntryId === entry.stock_entry_id}
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid={`assign-bin-${entry.stock_entry_id}`}
                          >
                            {repairingEntryId === entry.stock_entry_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <MapPin className="w-4 h-4 mr-1" />
                                Alocă
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default RepairStockPage;
